"use client";

import type { ReactNode } from "react";
import type { CalendarFestivalDetail } from "@/lib/api/calendar";
import { TYPE_LABELS, TYPE_LABELS_HI, TYPE_COLORS, formatDate } from "../_data";

type BodyLocale = "en" | "hi";

// Section copy per locale. The body is BILINGUAL data either way (the backend
// returns name_en/name_hi + description_en/description_hi); `locale` only decides
// which language LEADS (H1 + first paragraph + section labels), so the Hindi twin
// reads Hindi-first and the English page is unchanged.
const COPY = {
  en: {
    fastingDay: "Fasting Day",
    hinduMonth: "Hindu month",
    about: "About This Festival",
    significance: "Significance",
    pujaVidhi: "Puja Vidhi (How to Observe)",
    regionalNames: "Regional Names",
  },
  hi: {
    fastingDay: "व्रत का दिन",
    hinduMonth: "हिंदू मास",
    about: "इस पर्व के बारे में",
    significance: "महत्व",
    pujaVidhi: "पूजा विधि (कैसे मनाएँ)",
    regionalNames: "क्षेत्रीय नाम",
  },
} as const;

function DateCard({ year, dateStr }: { year: string; dateStr: string }) {
  return (
    <div
      style={{
        padding: "14px",
        borderRadius: "8px",
        background: "rgba(185, 100, 74, 0.06)",
        border: "1px solid rgba(185, 100, 74, 0.15)",
      }}
    >
      <div
        style={{
          fontSize: "12px",
          fontWeight: 600,
          color: "var(--color-accent)",
          marginBottom: "4px",
        }}
      >
        {year}
      </div>
      <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--ish-dark)" }}>
        {formatDate(dateStr)}
      </div>
    </div>
  );
}

export function FestivalBody({
  festival,
  locale = "en",
}: {
  festival: CalendarFestivalDetail;
  locale?: BodyLocale;
}) {
  return (
    <>
      <FestivalHeader festival={festival} locale={locale} />
      <FestivalDates festival={festival} />
      <FestivalDescription festival={festival} locale={locale} />
      <FestivalSignificance festival={festival} locale={locale} />
      <FestivalPujaVidhi festival={festival} locale={locale} />
      <FestivalRegionalNames festival={festival} locale={locale} />
    </>
  );
}

function FestivalHeader({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  const colors = TYPE_COLORS[festival.type] || TYPE_COLORS.festival;
  const copy = COPY[locale];
  const typeLabels = locale === "hi" ? TYPE_LABELS_HI : TYPE_LABELS;
  // Hindi twin leads with the Hindi name as the H1 (message-match); English page
  // keeps the English name as H1. The other-language name is the subtitle.
  const heading = locale === "hi" ? festival.name_hi : festival.name_en;
  const subheading = locale === "hi" ? festival.name_en : festival.name_hi;

  return (
    <div style={{ marginBottom: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
        <span
          style={{
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            color: colors.text,
            fontSize: "12px",
            padding: "2px 10px",
            borderRadius: "4px",
            fontWeight: 600,
          }}
        >
          {typeLabels[festival.type] || festival.type}
        </span>
        {festival.fasting && (
          <span
            style={{
              fontSize: "12px",
              background: "rgba(220, 38, 38, 0.1)",
              color: "var(--color-error-text)",
              padding: "2px 10px",
              borderRadius: "4px",
              fontWeight: 600,
            }}
          >
            {copy.fastingDay}
          </span>
        )}
      </div>

      <h1
        style={{
          fontFamily: "var(--font-pt-serif)",
          fontSize: "28px",
          fontWeight: 700,
          color: "var(--ish-dark)",
          margin: "0 0 4px",
          lineHeight: 1.2,
        }}
      >
        {heading}
      </h1>
      <p style={{ fontSize: "18px", color: "var(--ish-text-muted)", margin: "0 0 8px" }}>
        {subheading}
      </p>
      {festival.month_hi && (
        <p style={{ fontSize: "13px", color: "var(--ish-text-muted)", margin: 0 }}>
          {copy.hinduMonth}: <strong>{festival.month_hi}</strong>
        </p>
      )}
    </div>
  );
}

function FestivalDates({ festival }: { festival: CalendarFestivalDetail }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "12px",
        marginBottom: "24px",
      }}
    >
      {festival.date_2026 && <DateCard year="2026" dateStr={festival.date_2026} />}
      {festival.date_2027 && <DateCard year="2027" dateStr={festival.date_2027} />}
    </div>
  );
}

function FestivalDescription({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  // Lead with the locale's language; show the other as the secondary paragraph
  // (the backend returns both; only the lead order changes per locale).
  const lead = locale === "hi" ? festival.description_hi : festival.description_en;
  const secondary = locale === "hi" ? festival.description_en : festival.description_hi;
  return (
    <div style={{ marginBottom: "24px" }}>
      <FestivalSectionTitle>{COPY[locale].about}</FestivalSectionTitle>
      <p style={bodyTextStyle}>{lead}</p>
      {secondary && <p style={{ ...bodyTextStyle, margin: 0 }}>{secondary}</p>}
    </div>
  );
}

function FestivalSignificance({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  if (!festival.significance) return null;

  return (
    <div style={{ marginBottom: "24px" }}>
      <FestivalSectionTitle>{COPY[locale].significance}</FestivalSectionTitle>
      <p style={{ ...bodyTextStyle, margin: 0 }}>{festival.significance}</p>
    </div>
  );
}

function FestivalPujaVidhi({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  if (!festival.puja_vidhi) return null;

  return (
    <div
      style={{
        marginBottom: "24px",
        padding: "20px",
        borderRadius: "8px",
        background: "rgba(22, 163, 74, 0.04)",
        border: "1px solid rgba(22, 163, 74, 0.15)",
      }}
    >
      <FestivalSectionTitle color="var(--color-success-text)">
        {COPY[locale].pujaVidhi}
      </FestivalSectionTitle>
      <p style={{ ...bodyTextStyle, margin: 0 }}>{festival.puja_vidhi}</p>
    </div>
  );
}

function FestivalRegionalNames({
  festival,
  locale,
}: {
  festival: CalendarFestivalDetail;
  locale: BodyLocale;
}) {
  if (festival.regional_names.length === 0) return null;

  return (
    <div style={{ marginBottom: "24px" }}>
      <FestivalSectionTitle>{COPY[locale].regionalNames}</FestivalSectionTitle>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {festival.regional_names.map((name) => (
          <span key={name} style={regionalNameStyle}>
            {name}
          </span>
        ))}
      </div>
    </div>
  );
}

// FestivalProducts removed — physical storefront retired (digital-only 2026-06)

function FestivalSectionTitle({
  children,
  color = "var(--ish-dark)",
  fontSize = "18px",
}: {
  children: ReactNode;
  color?: string;
  fontSize?: string;
}) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-pt-serif)",
        fontSize,
        fontWeight: 700,
        color,
        marginBottom: "8px",
      }}
    >
      {children}
    </h2>
  );
}

const bodyTextStyle = {
  fontSize: "14px",
  lineHeight: "1.7",
  color: "var(--ish-text-muted)",
  margin: "0 0 8px",
};

const regionalNameStyle = {
  padding: "4px 12px",
  borderRadius: "4px",
  background: "rgba(0,0,0,0.04)",
  border: "1px solid rgba(0,0,0,0.08)",
  fontSize: "13px",
  color: "var(--ish-dark)",
};
